import { Popover, Transition } from "@headlessui/react";
import { StarIcon } from "@heroicons/react/solid";
import { useEffect, useRef, useState, Fragment } from "react";
import { createPortal } from "react-dom";
import { usePopper } from "react-popper";
import { PrepCenter, PrepCenterLocation } from "../model/prepCenter";
import { Marker } from "react-map-gl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "../util/classNames";
import Link from "next/link";
import Image from "next/image";

const getShortLocationString = (location: PrepCenterLocation): string => {
  let locationString = "";

  if (location.city) locationString += location.city + ", ";
  locationString += location.state + ", ";
  locationString += location.country;

  return locationString;
};

export default function LocationMarker({
  location,
  prepCenter,
  highlighted,
}: {
  location: PrepCenterLocation;
  prepCenter: PrepCenter;
  highlighted?: string;
}) {
  let popperElRef = useRef(null);
  let [targetElement, setTargetElement] = useState<HTMLButtonElement | null>(
    null
  );
  let [popperElement, setPopperElement] = useState(null);
  let { styles, attributes } = usePopper(targetElement, popperElement, {
    placement: "top",
  });

  const [showPopover, setShowPopover] = useState(false);
  const [onClient, setOnClient] = useState(false);

  useEffect(() => {
    setOnClient(true);
  }, [setOnClient]);

  return (
    <Marker
      longitude={location.longitude as number}
      latitude={location.latitude as number}
      style={{ zIndex: highlighted === location.prepCenterId ? 10 : 0 }}
    >
      <Popover as="div">
        <Popover.Button ref={setTargetElement}>
          <div
            onMouseEnter={() => setShowPopover(true)}
            onMouseLeave={() => setShowPopover(false)}
            className={classNames(
              "cursor-pointer inline-flex items-center justify-center h-7 w-7 rounded-full border-2 shadow-sm",
              highlighted === location.prepCenterId
                ? "bg-green-600 ring-4 ring-offset-2 ring-green-500"
                : "bg-vendrive-base hover:bg-green-600 border-vendrive-500 hover:border-green-500"
              // highlighted && highlighted !== location.prepCenterId
              //   ? "opacity-50"
              //   : ""
            )}
          >
            <FontAwesomeIcon
              icon={["fad", "forklift"]}
              className="text-xl h-4 w-4 text-white"
            />
          </div>
        </Popover.Button>

        {onClient &&
          createPortal(
            <div
              ref={popperElRef}
              style={styles.popper}
              {...attributes.popper}
              className="z-50 p-2 max-w-sm w-full"
            >
              <Transition
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
                beforeEnter={() => setPopperElement(popperElRef.current)}
                afterLeave={() => setPopperElement(null)}
                show={showPopover}
                onMouseEnter={() => setShowPopover(true)}
                onMouseLeave={() => setShowPopover(false)}
              >
                <Popover.Panel
                  static
                  className="mb-1 rounded-md bg-white dark:bg-neutral-base shadow-lg ring-1 ring-neutral-100 dark:ring-neutral-600 text-neutral-900 dark:text-white text-sm"
                >
                  <Link href={`/p/${location.prepCenterId}`} passHref>
                    <a className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                      <div className="relative bg-white p-2">
                        <div className="flex items-center rounded-lg p-2 transition duration-150 ease-in-out">
                          <div
                            className={classNames(
                              "flex h-10 w-10 shrink-0 items-center justify-center text-black sm:h-14 sm:w-14 rounded-md",
                              prepCenter.logo
                                ? "p-1 bg-gray-100"
                                : "bg-gray-300 p-3"
                            )}
                          >
                            <div className="relative flex h-full w-full rounded-sm">
                              <Image
                                src={
                                  prepCenter.logo ?? "/images/icon-light.svg"
                                }
                                alt={prepCenter.name}
                                layout="fill"
                                objectFit="contain"
                                className="rounded-sm"
                              />
                            </div>
                          </div>
                          <div className="ml-4">
                            <p className="text-sm font-medium text-gray-900">
                              {prepCenter.name}
                            </p>
                            <p className="text-sm text-gray-500">
                              {getShortLocationString(location)}
                            </p>
                            <div className="flex items-center mt-1">
                              {[0, 1, 2, 3, 4].map((rating) => (
                                <StarIcon
                                  key={rating}
                                  className={classNames(
                                    Math.round(prepCenter.averageRating ?? 0) >
                                      rating
                                      ? "text-yellow-400"
                                      : "text-gray-300",
                                    "h-5 w-5 flex-shrink-0"
                                  )}
                                  aria-hidden="true"
                                />
                              ))}
                              <span className="text-gray-500 text-sm ml-2">
                                {prepCenter.reviewCount
                                  ? `${prepCenter.averageRating?.toFixed(2)} (${
                                      prepCenter.reviewCount
                                    } Review${
                                      prepCenter.reviewCount > 1 ? "s" : ""
                                    })`
                                  : "No reviews"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </a>
                  </Link>
                </Popover.Panel>
              </Transition>
            </div>,
            document.body
          )}
      </Popover>
    </Marker>
  );
}
